import React, { lazy, Suspense } from "react";
import { Navigate, redirect } from "react-router-dom";
import { toast } from "react-toastify";
import Root from "./Root";
import Dashboard from "./Dashboard/Dashboard";
import { proxyAPI } from "./proxyAPI";
import logo from "../assets/boom_coloured.png";
import BoomLogoIcon from "../assets/icons/boomerang_element-01.svg2";
import { isUserPermitted } from "./helpers/pageProps";
import SchedulersList from "./Schedulers/SchedulersList";
import Solutions from "./Products/ProductsSolutions";
import ProductsBoomlogic from "./Products/ProductsBoomlogic";
import ProductsSolutions from "./Products/ProductsSolutions";
import ProductsBoomAI from "./Products/ProductsBoomAI";
import ProductsBoomMail from "./Products/ProductsBoomMail";
import ProductsBroadcastMessaging from "./Products/ProductsBroadcastMessaging";
import ProductsCampaign from "./Products/ProductsCampaign";
import ProductsMonitoringAndAlerting from "./Products/ProductsMonitoringAndAlerting";
import ProductsEventsAndAlerting from "./Products/ProductsEventsAndAlering";
import ProductsProfessionalBuilder from "./Products/ProductsProfessionalBuilder";
import ProductsLandingPage from "./Products/ProductsLandingPage";
import ProductsApiSPlugingsAndApps from "./Products/ProductsApiSPlugingsAndApps";
import ProductsWeatherMonitoringAndAlerting from "./Products/ProductsWeatherMonitoringAndAlerting";
import ProductsAssetManagement from "./Products/ProductsAssetManagement";

// Lazy-loaded route components
const ComponentCards = lazy(() =>
  import("./ProjectsOverview/Projects/ComponentCards")
);
const Processes = lazy(() => import("./ProjectsOverview/Processes/Processes"));
const Process = lazy(() => import("./ProjectsOverview/Processes/Process"));
const WorkflowInbounds = lazy(() => import("./Inbounds/WorkflowInbounds"));
const EmptyProject = lazy(() => import("./Builder/EmptyProject"));
const ProjectsMenu = lazy(() => import("./Builder/Components/ProjectsMenu"));
const Diagrams = lazy(() => import("./Builder/Diagrams"));
const ProjectSettings = lazy(() =>
  import("./Builder/Components/ProjectSettings")
);
const Errors = lazy(() => import("./Builder/Components/ErrorsSidebar"));
const InheritedContactSets = lazy(() =>
  import("./Builder/Components/modals/InheritedContactSets")
);
const WorkflowModal = lazy(() => import("./Builder/nodes/Logic/WorkflowModal"));
const MessageTemplate = lazy(() =>
  import("./Builder/Components/modals/MessageTemplate")
);
const MessageConfig = lazy(() =>
  import("./Builder/Components/modals/MessageConfig")
);
const DataCategory = lazy(() =>
  import("./Builder/Components/modals/DataCategory")
);
const BusinessHours = lazy(() =>
  import("./Builder/Components/modals/BusinessHours")
);
const CalendarWidget = lazy(() =>
  import("./Builder/Components/Calendar/CalendarWidget")
);
const ContactEditPage = lazy(() => import("./Contacts/ContactEditPage"));
const AddBulk = lazy(() => import("./Contacts/AddBulk"));
const ContactFields = lazy(() => import("./Contacts/ContactFields"));
const ContactSetPage = lazy(() => import("./Contacts/ContactSetPage"));
const BroadcastPage = lazy(() => import("./Broadcast/BroadcastPage"));
const AnalyticalReportingPage = lazy(() =>
  import("./Reporting/AnalyticalReportingPage")
);
const BillingStatus = lazy(() => import("./Billing/BillingStatus"));
const RealtimeReportingPage = lazy(() =>
  import("./Reporting/RealtimeReportingPage")
);
const ReportingTopics = lazy(() => import("./Reporting/ReportingTopics"));
const ReportingTemplates = lazy(() => import("./Reporting/ReportingTemplates"));
const SystemReportingPage = lazy(() =>
  import("./Reporting/SystemReportingPage")
);
const TicketReportingPage = lazy(() =>
  import("./Reporting/TicketReportingPage")
);
const ContactSetsPage = lazy(() => import("./Contacts/ContactSetsPage"));
const ContactsPage = lazy(() => import("./Contacts/ContactsPage"));
const ProjectSetupWizard = lazy(() =>
  import("./ProjectBuilder/ProjectSetupWizard")
);
const LogicTemplateLibrary = lazy(() =>
  import("./Library/LogicTemplateLibrary")
);
const DocumentTemplateLibrary = lazy(() =>
  import("./Library/DocumentTemplateLibrary")
);
const BroadcastTemplateLibrary = lazy(() =>
  import("./Library/BroadcastTemplateLibrary")
);
const ProjectBuilder = lazy(() => import("./ProjectBuilder/ProjectBuilder"));
const Project = lazy(() => import("./ProjectBuilder/Project"));
const BillingOverview = lazy(() => import("./Billing/BillingOverview"));
const BillingInboundNumbers = lazy(() =>
  import("./Billing/BillingInboundNumbers")
);
const BillingPaymentMethods = lazy(() =>
  import("./Billing/BillingPaymentMethods")
);
const BillingDetails = lazy(() => import("./Billing/BillingDetails"));
const BillingInvoiceHistory = lazy(() =>
  import("./Billing/BillingInvoiceHistory")
);
const Checkout = lazy(() => import("./Dashboard/Checkout"));
const ImportedContactFilesLibrary = lazy(() =>
  import("./Library/ImportedContactFilesLibrary")
);
const ComponentLibrary = lazy(() => import("./Library/ComponentLibrary"));
const APIDataCollectionLibrary = lazy(() =>
  import("./Library/APIDataCollectionLibrary")
);
const ApiProfileLibrary = lazy(() => import("./Library/ApiProfileLibrary"));
const ApiProfile = lazy(() => import("./API/ApiProfile"));
const LibraryLandingPage = lazy(() => import("./Library/LibraryLandingPage"));
const ReportingLandingPage = lazy(() =>
  import("./Reporting/ReportingLandingPage")
);
const ContactsLandingPage = lazy(() =>
  import("./Contacts/ContactsLandingPage")
);
const BillingSettings = lazy(() => import("./Settings/BillingSettings"));
const SecuritySettings = lazy(() => import("./Settings/SecuritySettings"));
const Interactions = lazy(() => import("./Interactions/Interactions"));
const MapPage = lazy(() => import("./Maps/MapPage"));
const Assets = lazy(() => import("./Assets/Assets"));
const UsersSettings = lazy(() => import("./Settings/UsersSettings"));
const AccountOverviewSettings = lazy(() =>
  import("./Settings/AccountOverviewSettings")
);
const OrganisationSettings = lazy(() =>
  import("./Settings/OrganisationSettings")
);
const CommunicationsSettings = lazy(() =>
  import("./Settings/CommunicationsSettings")
);
const AssetsLandingPage = lazy(() => import("./Assets/AssetsLandingPage"));
const ReportingAssets = lazy(() => import("./Reporting/ReportingAssets"));
const Monitoring = lazy(() => import("./Monitoring/MonitoringLandingPage"));
const ApiConnectionLibrary = lazy(() =>
  import("./Library/ApiConnectionLibrary")
);
const EventsPage = lazy(() => import("./Events/EventsPage"));
const EventsReportingPage = lazy(() =>
  import("./Reporting/EventsReportingPage")
);
const CreateNewMonitorModal = lazy(() =>
  import("./Monitoring/CreateNewMonitorModal")
);
const UserRoleEditPage = lazy(() => import("./Settings/UserRoleEditPage"));
const SolutionsStore = lazy(() => import("./Library/SolutionsStore"));
const SolutionDetails = lazy(() => import("./Solutions/SolutionDetails"));
const SystemAdminSettings = lazy(() =>
  import("./Settings/SystemAdminSettings")
);
const BillingLinks = lazy(() => import("./Billing/BillingLinks"));
const BillingLandingPage = lazy(() => import("./Billing/BillingLandingPage"));
const SettingsLandingPage = lazy(() =>
  import("./Settings/SettingsLandingPage")
);
const CreateSolutionPage = lazy(() =>
  import("./SolutionsBuilder/CreateSolutionPage")
);

// Wraps a lazy component in Suspense with a loading spinner
const S = (Component) => (
  <Suspense
    fallback={
      <div className="d-flex justify-content-center p-5">
        <div className="spinner-border text-primary" role="status" />
      </div>
    }
  >
    <Component />
  </Suspense>
);

const { baseUrl: baseURL } = JSON.parse(
  document.querySelector("#app").dataset.page
).props;

const processLoader = async ({ params }) => {
  if (params.projectID === undefined) {
    return null;
  }

  const data = await proxyAPI({
    prefix: "logic/workflows/api",
    url: `/info?project=${params.projectID}${
      params.processID ? `&process=${params.processID}` : ""
    }`,
  });
  return data;
};

const projectLoader = async ({ params }) => {
  if (params.projectID === undefined) {
    return null;
  }

  if (params.projectID === "new") {
    return { Label: "New project" };
  }

  const data = await proxyAPI({
    prefix: "logic/workflows/api",
    url: `/info?project=${params.projectID}`,
  });

  return data;
};

const importAction = async ({ request }) => {
  const { importedProject, csrfToken } = await request.json();
  const response = await fetch(`${baseURL}/workflows-api/project`, {
    method: "POST",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-TOKEN": csrfToken,
    },
    body: JSON.stringify(importedProject),
  });

  const data = await response.json();
  if (!response.ok) {
    return {
      error: `There was an error: ${data.message} (${response.status} - ${response.statusText})`,
    };
  }
  toast.success(`Project ${data.Label} has been imported.`);
  return redirect(`/${data.ID}`);
};

const routes = [
  {
    path: "/",
    element: <Root />,
    handle: {
      crumb: () => ({
        href: "/",
        label: <BoomLogoIcon style={{ width: "24px", height: "24px" }} />,
      }),
    },
    children: [
      // dashboard
      {
        index: true,
        element: <Dashboard />,
        handle: {
          crumb: () => ({ href: "/", label: "Dashboard" }),
        },
      },
      {
        path: "/checkout",
        element: S(Checkout),
        handle: {
          crumb: () => ({
            href: `/checkout`,
            label: `Checkout`,
          }),
        },
      },

      // reporting
      {
        path: "/reporting",
        element: null,
        handle: {
          crumb: () => ({
            href: `/reporting`,
            label: `Report`,
          }),
        },
        children: [
          {
            index: true,
            element: S(ReportingLandingPage),
            handle: {
              crumb: () => ({
                href: `/reporting`,
                label: `Home`,
              }),
            },
          },
          {
            path: "topics",
            element: S(ReportingTopics),
            handle: {
              crumb: () => ({
                href: `/topics`,
                label: `Reporting topics`,
              }),
            },
          },
          {
            path: "templates",
            element: S(ReportingTemplates),
            handle: {
              crumb: () => ({
                href: `/templates`,
                label: `Reporting templates`,
              }),
            },
          },
          {
            path: "analytical",
            element: S(AnalyticalReportingPage),
            handle: {
              crumb: () => ({
                href: `/analytical`,
                label: `Analytical report`,
              }),
            },
          },
          {
            path: "assets",
            element: S(ReportingAssets),
            handle: {
              crumb: () => ({
                href: `/assets`,
                label: `Assets Report`,
              }),
            },
          },
          {
            path: "events",
            element: S(EventsReportingPage),
            handle: {
              crumb: () => ({
                href: `/events`,
                label: `Events Reporting`,
              }),
            },
          },
          {
            path: "realtime",
            element: S(RealtimeReportingPage),
            children: [{ path: ":realtimeTab?", element: null }],
            handle: {
              crumb: () => ({
                href: `/realtime`,
                label: `Real-time report`,
              }),
            },
          },
          {
            path: "ticketing/:tab?",
            element: S(TicketReportingPage),
            handle: {
              crumb: () => ({
                href: `/ticketing`,
                label: `Ticket`,
              }),
            },
          },
          {
            path: "system",
            element: S(SystemReportingPage),
            handle: {
              crumb: () => ({
                href: `/system`,
                label: `System report`,
              }),
            },
          },
          {
            path: "processes",
            handle: {
              crumb: () => ({
                href: `/reporting/processes`,
                label: `Processes`,
              }),
            },
            children: [
              {
                path: ":projectID?",
                loader: processLoader,
                handle: {
                  crumb: (match) =>
                    match.params.projectID && {
                      href: `/reporting/processes/${match.params.projectID}`,
                      label: `Project: ${match.data.Label}`,
                    },
                },
                children: [
                  {
                    index: true,
                    element: S(Processes),
                  },
                  {
                    path: "process/:processID",
                    element: S(Process),
                    loader: processLoader,
                    handle: {
                      crumb: (match) =>
                        match.params.processID && {
                          href: `/reporting/processes/${match.params.projectID}/process/${match.params.processID}`,
                          label: `Process: ${match.data.Guid}`,
                        },
                    },
                  },
                ],
              },
            ],
          },
        ],
      },

      // library
      {
        path: "/library",
        handle: {
          crumb: () => ({
            href: `/library`,
            label: `Library`,
          }),
        },
        children: [
          {
            index: true,
            element: S(LibraryLandingPage),
          },
          {
            path: "logic",
            element: null,
            handle: {
              crumb: () => ({
                href: `/library/logic`,
                label: `Logic`,
              }),
            },
            children: [
              {
                index: true,
                element: S(LogicTemplateLibrary),
              },
              // advanced builder
              {
                path: "builder",
                handle: {
                  crumb: () => ({
                    href: "/library/logic/builder",
                    label: "Builder",
                  }),
                },
                children: [
                  {
                    index: true,
                    element: S(EmptyProject),
                    action: importAction,
                  },
                  {
                    path: "projects",
                    element: S(ProjectsMenu),
                  },
                  {
                    path: ":projectID",
                    element: S(Diagrams),
                    loader: projectLoader,
                    handle: {
                      crumb: (match) =>
                        match.data
                          ? {
                              href: `/library/logic/builder/${match.data.ID}`,
                              label: match.data.Label,
                            }
                          : { href: null, label: "New project" },
                    },
                    children: [
                      {
                        path: "settings",
                        element: S(ProjectSettings),
                      },
                      {
                        path: "errors",
                        element: S(Errors),
                      },
                      {
                        path: "workflow/:workflowUUID/inherited-from",
                        element: S(InheritedContactSets),
                      },
                      {
                        path: "workflow/:workflowUUID",
                        element: S(WorkflowModal),
                        children: [
                          { path: ":workflowTab?", element: null },
                          {
                            path: "event/:event",
                            element: null,
                            children: [
                              {
                                path: "listener/:listenerUUID",
                                element: null,
                                children: [
                                  {
                                    path: "action/:actionUUID",
                                    element: null,
                                  },
                                ],
                              },
                            ],
                          },
                        ],
                      },
                      // {
                      //   path: "input-argument/:inputArgumentUUID",
                      //   element: <InputArgument />,
                      // },
                      {
                        path: "message-template/:messageTemplateUUID/:messageTemplateTab?",
                        element: S(MessageTemplate),
                      },
                      {
                        path: "message-config/:messageConfigUUID",
                        element: S(MessageConfig),
                      },
                      {
                        path: "category/:type/:category",
                        element: S(DataCategory),
                      },
                      {
                        path: "business-hours/:businessHoursUUID",
                        element: S(BusinessHours),
                      },
                      {
                        path: "calendar/:calendarUUID",
                        element: S(CalendarWidget),
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: "document",
            index: true,
            element: S(DocumentTemplateLibrary),
            handle: {
              crumb: () => ({
                href: `/library/document`,
                label: `Document`,
              }),
            },
          },
          {
            path: "broadcast",
            handle: {
              crumb: () => ({
                href: `/library/broadcast`,
                label: `Broadcast`,
              }),
            },
            children: [
              {
                index: true,
                element: S(BroadcastTemplateLibrary),
              },
              {
                path: ":broadcastUUID/:broadcastPage?",
                element: S(BroadcastPage),
                handle: {
                  crumb: (match) =>
                    match.params.broadcastPage && {
                      href: `/library/broadcast/${match.params.broadcastPage}`,
                      label:
                        match.params.broadcastPage.charAt(0).toUpperCase() +
                        match.params.broadcastPage.slice(1),
                    },
                },
              },
            ],
          },
          {
            path: "imported-contact-files",
            index: true,
            element: S(ImportedContactFilesLibrary),
            handle: {
              crumb: () => ({
                href: `/library/imported-contact-files`,
                label: `Imported Contact Files`,
              }),
            },
          },
          {
            path: "api-profiles",
            handle: {
              crumb: () => ({
                href: `/library/api-profiles`,
                label: `API Profiles`,
              }),
            },
            children: [
              {
                index: true,
                element: S(ApiProfileLibrary),
              },
              {
                path: "add",
                element: S(ApiProfile),
                handle: {
                  crumb: () => ({
                    href: `/library/api-profiles/add`,
                    label: `Add API profile`,
                  }),
                },
              },
              {
                path: ":apiProfileUUID",
                element: S(ApiProfile),
                handle: {
                  crumb: () => ({
                    href: `/library/api-profiles`,
                    label: `API profile`,
                  }),
                },
              },
            ],
          },
          {
            path: "api-data-collections",
            handle: {
              crumb: () => ({
                href: `/library/api-data-collections`,
                label: `API Data Collections`,
              }),
            },
            children: [
              {
                index: true,
                element: S(APIDataCollectionLibrary),
              },
              // {
              //   path: "add",
              //   element: <ApiDataCollection />,
              //   handle: {
              //     crumb: () => ({
              //       href: `/library/api-data-collections/add`,
              //       label: `Add API data collection`,
              //     }),
              //   },
              // },
              // {
              //   path: ":apiDataCollectionUUID",
              //   element: <ApiDataCollection />,
              //   handle: {
              //     crumb: () => ({
              //       href: `/api/data-collections`,
              //       label: `API data collection`,
              //     }),
              //   },
              // },
            ],
          },
          {
            path: "api-connections",
            element: S(ApiConnectionLibrary),
            handle: {
              crumb: () => ({
                href: `/library/api-connections`,
                label: `API Connections`,
              }),
            },
            children: [
              {
                index: true,
              },
              {
                path: "add",
                handle: {
                  crumb: () => ({
                    href: `/library/api-connections/add`,
                    label: `Add API connection`,
                  }),
                },
              },
              {
                path: ":connectionUUID/:selectedTabName?",
                handle: {
                  crumb: () => ({
                    href: `/library/api-connections`,
                    label: `API connection`,
                  }),
                },
              },
            ],
          },
          {
            path: "campaigns",
            index: true,
            element: S(WorkflowInbounds),
            handle: {
              crumb: () => ({
                href: `/library/campaigns`,
                label: `Campaigns`,
              }),
            },
          },
          // projects
          {
            path: "projects",
            element: S(ComponentCards),
            handle: {
              crumb: () => ({
                href: `/library/projects`,
                label: `Projects`,
              }),
            },
          },
          // solutions
          {
            // backwards compatibility for old links
            path: "components",
            element: S(SolutionsStore),
            handle: {
              crumb: () => ({
                href: `/library/solutions`,
                label: `Solutions`,
              }),
            },
          },
          {
            path: "solutions",
            handle: {
              crumb: () => ({
                href: `/library/solutions`,
                label: `Solutions`,
              }),
            },
            children: [
              { index: true, element: S(SolutionsStore) },

              {
                // store
                path: "store",
                handle: {
                  crumb: () => ({
                    href: `/library/solutions/store`,
                    label: `Store`,
                  }),
                },
                element: S(SolutionsStore),
              },
              {
                // details
                path: ":solutionUuid",
                handle: {
                  crumb: () => ({
                    href: `/library/solutions/details`,
                    label: `Details`,
                  }),
                },
                element: S(SolutionDetails),
              },
              {
                path: ":solutionUuid/edit",
                handle: {
                  crumb: () => ({
                    href: `/library/solutions/`,
                    label: `Edit`,
                  }),
                },
                element: S(CreateSolutionPage),
              },
            ],
          },
        ],
      },

      // create
      {
        path: "/create",

        handle: {
          crumb: () => ({ href: "/create", label: "Create" }),
        },
        children: [
          { index: true, element: <Navigate to={"/"} replace /> },

          // project builder
          {
            path: "project",
            handle: {
              crumb: () => ({
                href: "/create/project/wizard/attributes",
                label: "Project",
              }),
            },
            element: S(Project),
            children: [
              {
                path: "wizard/:page",
                handle: {
                  crumb: () => ({
                    href: `/create/project/wizard/attributes`,
                    label: `Q&A Setup Wizard`,
                  }),
                },
                element: S(ProjectSetupWizard),
              },
              {
                path: "builder",
                handle: {
                  crumb: () => ({
                    href: "/create/project/builder",
                    label: "Project Builder",
                  }),
                },
                element: S(ProjectBuilder),
              },
              {
                path: "overview",
                handle: {
                  crumb: () => ({
                    href: "/create/project/overview",
                    label: "Project Overview",
                  }),
                },
                element: <>Project overview</>,
              },
            ],
          },
          {
            path: "broadcast",
            handle: {
              crumb: () => ({
                href: `/library/broadcast/new/settings`,
                label: `Broadcast`,
              }),
            },
            element: S(BroadcastPage),
            children: [
              {
                path: ":broadcastUUID/:broadcastPage?",
                handle: {
                  crumb: (match) =>
                    match.params.broadcastPage && {
                      href: `/library/broadcast/${match.params.broadcastPage}`,
                      label:
                        match.params.broadcastPage.charAt(0).toUpperCase() +
                        match.params.broadcastPage.slice(1),
                    },
                },
              },
            ],
          },
        ],
      },

      // billing
      {
        path: "/billing",
        element: null,
        handle: {
          crumb: () => ({
            href: `/billing`,
            label: `Billing`,
          }),
        },
        children: [
          {
            index: true,
            element: S(BillingLandingPage),
            handle: {
              crumb: () => ({
                href: `/status`,
                label: `Status`,
              }),
            },
          },
          {
            path: "status",
            element: S(BillingStatus),
            handle: {
              crumb: () => ({
                href: `/status`,
                label: `Status`,
              }),
            },
          },
          {
            path: "overview",
            element: S(BillingOverview),
            handle: {
              crumb: () => ({
                href: `/overview`,
                label: `Overview`,
              }),
            },
          },
          {
            path: "inbounds",
            element: S(BillingInboundNumbers),
            handle: {
              crumb: () => ({
                href: `/inbounds`,
                label: `Inbound Numbers`,
              }),
            },
          },
          {
            path: "payment",
            element: S(BillingPaymentMethods),
            handle: {
              crumb: () => ({
                href: `/payment`,
                label: `Payment Methods`,
              }),
            },
          },
          {
            path: "details",
            element: S(BillingDetails),
            handle: {
              crumb: () => ({
                href: `/details`,
                label: `Billing Details`,
              }),
            },
          },
          {
            path: "invoices",
            element: S(BillingInvoiceHistory),
            handle: {
              crumb: () => ({
                href: `/billing/invoices`,
                label: `Invoices`,
              }),
            },
          },
        ],
      },

      // inbounds
      {
        path: "/inbounds",
        handle: {
          crumb: () => ({ href: "/inbounds", label: "Inbounds" }),
        },
        element: S(WorkflowInbounds),
        children: [
          {
            path: ":inboundUUID",
            element: null,
            handle: {
              crumb: () => ({ label: "Edit Inbound" }),
            },
          },
        ],
      },

      // settings
      ...(isUserPermitted("view_system_setting")
        ? [
            {
              path: "/settings",
              handle: {
                crumb: () => ({ href: "/settings", label: "Settings" }),
              },
              children: [
                {
                  index: true,
                  element: S(SettingsLandingPage),
                  handle: {
                    crumb: () => ({
                      href: "/settings",
                      label: "Communications",
                    }),
                  },
                },
                {
                  path: "communications",
                  index: true,
                  element: S(CommunicationsSettings),
                  handle: {
                    crumb: () => ({
                      href: "/settings",
                      label: "Communications",
                    }),
                  },
                },
                {
                  path: "billing",

                  element: S(BillingSettings),
                  handle: {
                    crumb: () => ({ href: "/settings", label: "Billing" }),
                  },
                },
                {
                  path: "security",

                  element: S(SecuritySettings),
                  handle: {
                    crumb: () => ({ href: "/settings", label: "Security" }),
                  },
                },
                {
                  path: "organisation",

                  element: S(OrganisationSettings),
                  handle: {
                    crumb: () => ({
                      href: "/settings",
                      label: "Organisation ",
                    }),
                  },
                },

                {
                  path: "accountoverview",

                  element: S(AccountOverviewSettings),
                  handle: {
                    crumb: () => ({
                      href: "/settings",
                      label: "Account Overview",
                    }),
                  },
                },
                {
                  path: "systemadmin",

                  element: S(SystemAdminSettings),
                  handle: {
                    crumb: () => ({ href: "/settings", label: "System Admin" }),
                  },
                },
                {
                  path: "users",
                  handle: {
                    crumb: () => ({ href: "/settings/users", label: "Users " }),
                  },
                  children: [
                    {
                      index: true,
                      element: S(UsersSettings),
                      handle: {
                        crumb: () => ({
                          href: "/settings/users",
                          label: "Users ",
                        }),
                      },
                    },
                    {
                      path: "role/:roleID",

                      element: S(UserRoleEditPage),
                      handle: {
                        crumb: () => ({
                          href: "/settings/users/role",
                          label: "Role ",
                        }),
                      },
                    },
                  ],
                },
              ],
            },
          ]
        : []),

      // interactions
      {
        path: "/interactions",
        handle: {
          crumb: () => ({ href: "/interactions", label: "Interactions" }),
        },
        element: S(Interactions),
      },
      {
        path: "/monitoring",
        handle: {
          crumb: () => ({ href: "/monitoring", label: "Monitoring" }),
        },
        element: S(Monitoring),
        children: [
          {
            path: ":monitoringID",
            element: S(CreateNewMonitorModal),
            handle: {
              crumb: (match) => ({
                href: `/monitoring`,
                label: "Monitoring Detail",
              }),
            },
          },
        ],
      },

      // advanced builder
      {
        path: "/builder",
        handle: {
          crumb: () => ({ href: "/builder", label: "Builder" }),
        },
        children: [
          {
            index: true,
            element: S(EmptyProject),
            action: importAction,
          },
          {
            path: "projects",
            element: S(ProjectsMenu),
          },
          {
            path: ":projectID",
            element: S(Diagrams),
            loader: projectLoader,
            handle: {
              crumb: (match) =>
                match.data
                  ? {
                      href: `/library/logic/builder/${match.data.ID}`,
                      label: match.data.Label,
                    }
                  : { href: null, label: "New project" },
            },
            children: [
              {
                path: "settings",
                element: S(ProjectSettings),
              },
              {
                path: "errors",
                element: S(Errors),
              },
              {
                path: "workflow/:workflowUUID/inherited-from",
                element: S(InheritedContactSets),
              },
              {
                path: "workflow/:workflowUUID",
                element: S(WorkflowModal),
                children: [
                  { path: ":workflowTab?", element: null },
                  {
                    path: "event/:event",
                    element: null,
                    children: [
                      {
                        path: "listener/:listenerUUID",
                        element: null,
                        children: [
                          {
                            path: "action/:actionUUID",
                            element: null,
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              // {
              //   path: "input-argument/:inputArgumentUUID",
              //   element: <InputArgument />,
              // },
              {
                path: "message-template/:messageTemplateUUID/:messageTemplateTab?",
                element: S(MessageTemplate),
              },
              {
                path: "message-config/:messageConfigUUID",
                element: S(MessageConfig),
              },
              {
                path: "category/:type/:category",
                element: S(DataCategory),
              },
              {
                path: "business-hours/:businessHoursUUID",
                element: S(BusinessHours),
              },
              {
                path: "calendar/:calendarUUID",
                element: S(CalendarWidget),
              },
            ],
          },
        ],
      },
      {
        path: "maps",
        handle: {
          crumb: () => ({ href: "/maps", label: "Maps" }),
        },
        element: S(MapPage),
      },
      {
        path: "assets",
        handle: {
          crumb: () => ({ href: "/assets", label: "Assets" }),
        },
        children: [
          { index: true, element: S(AssetsLandingPage) },
          {
            path: "assets",
            element: S(Assets),
            children: [{ path: ":assetUUID", element: null }],
            handle: {
              crumb: () => ({ href: "/assets", label: "Assets" }),
            },
          },
          {
            path: "maps",
            handle: {
              crumb: () => ({ href: "/assets/maps", label: "Maps" }),
            },
            element: S(MapPage),
          },
          {
            path: "events",
            handle: {
              crumb: () => ({ href: "/assets/events", label: "Events" }),
            },

            element: S(EventsPage),
          },
          {
            path: "schedulers",
            handle: {
              crumb: () => ({
                href: "/assets/schedulers",
                label: "Schedulers",
              }),
            },

            element: S(SchedulersList),
          },
          // contacts
          {
            path: "contacts",
            handle: {
              crumb: () => ({
                href: `/assets/contacts`,
                label: `Contacts`,
              }),
            },
            children: [
              {
                index: true,
                element: S(ContactsLandingPage),
                handle: {
                  crumb: () => ({
                    href: `/assets/contacts/`,
                    label: `Home`,
                  }),
                },
              },
              {
                path: "all",
                element: S(ContactsPage),
                handle: {
                  crumb: () => ({
                    href: `/assets/contacts/all`,
                    label: `All contacts`,
                  }),
                },
              },
              {
                path: "edit",
                element: <Navigate to="/assets/contacts/all" replace />,
              },
              {
                path: "edit/:contactUUID",
                element: S(ContactEditPage),
                handle: {
                  crumb: (match) => ({
                    href: `/assets/contacts/edit/${match.params.contactUUID}`,
                    label: `Edit contact`,
                  }),
                },
              },

              {
                path: "groups",

                handle: {
                  crumb: () => ({
                    href: `/assets/contacts/groups`,
                    label: `Groups`,
                  }),
                },
                children: [
                  { index: true, element: S(ContactSetsPage) },
                  {
                    path: ":contactSetUUID",
                    element: S(ContactSetPage),
                    handle: {
                      crumb: (match) => ({
                        href: `/assets/contacts/groups/${match.params.contactSetUUID}`,
                        label: `Group`,
                      }),
                    },
                  },
                ],
              },
              {
                path: "add",
                element: S(ContactEditPage),
                handle: {
                  crumb: () => ({
                    href: `/assets/contacts/add`,
                    label: `New contact`,
                  }),
                },
              },
              {
                path: "bulk",
                element: S(AddBulk),
                handle: {
                  crumb: () => ({
                    href: `/assets/contacts/bulk`,
                    label: `Add bulk`,
                  }),
                },
              },
              {
                path: "fields",
                element: S(ContactFields),
                handle: {
                  crumb: () => ({
                    href: `/assets/contacts/fields`,
                    label: `Fields`,
                  }),
                },
              },
            ],
          },
        ],
      },
      {
        path: "events",
        handle: {
          crumb: () => ({ href: "/events", label: "Events" }),
        },

        element: S(EventsPage),
      },

      {
        path: "*",
        element: (
          <div className="alert alert-danger text-center container my-5">
            Page not found
          </div>
        ),
      },
      // products
      {
        path: "products",
        handle: {
          crumb: () => ({ href: "/products", label: "Products" }),
        },
        children: [
          { index: true, element: S(ProductsLandingPage) },
          {
            path: "solutions",
            handle: {
              crumb: () => ({
                href: "/products/solutions",
                label: "Solutions",
              }),
            },
            element: S(ProductsSolutions),
          },
          {
            path: "boomlogic",
            handle: {
              crumb: () => ({
                href: "/products/boomlogic",
                label: "BoomLogic",
              }),
            },
            element: S(ProductsBoomlogic),
          },
          {
            path: "boomai",
            handle: {
              crumb: () => ({
                href: "/products/boomai",
                label: "BoomAI",
              }),
            },
            element: S(ProductsBoomAI),
          },
          {
            path: "boomMail",
            handle: {
              crumb: () => ({
                href: "/products/boomMail",
                label: "BoomMail",
              }),
            },
            element: S(ProductsBoomMail),
          },

          {
            path: "broadcastMessaging",
            handle: {
              crumb: () => ({
                href: "/products/broadcastMessaging",
                label: "Broadcast Messaging",
              }),
            },
            element: S(ProductsBroadcastMessaging),
          },
          {
            path: "campaign",
            handle: {
              crumb: () => ({
                href: "/products/campaign",
                label: "Campaign",
              }),
            },
            element: S(ProductsCampaign),
          },

          {
            path: "apisPluginsandApps",
            handle: {
              crumb: () => ({
                href: "/products/apisPluginsandApps",
                label: "API’s plugins and Apps",
              }),
            },
            element: S(ProductsApiSPlugingsAndApps),
          },
          {
            path: "weatherMonitoringAndAlerting",
            handle: {
              crumb: () => ({
                href: "/products/weatherMonitoringAndAlerting",
                label: "Weather Monitoring and Alerting",
              }),
            },
            element: S(ProductsWeatherMonitoringAndAlerting),
          },
          {
            path: "assetManagement",
            handle: {
              crumb: () => ({
                href: "/products/assetManagement",
                label: "Asset Management",
              }),
            },
            element: S(ProductsAssetManagement),
          },
          {
            path: "monitoringAndAlerting",
            handle: {
              crumb: () => ({
                href: "/products/monitoringAndAlerting",
                label: "Monitoring and Alerting",
              }),
            },
            element: S(ProductsMonitoringAndAlerting),
          },
          {
            path: "eventsAndAlerting",
            handle: {
              crumb: () => ({
                href: "/products/eventsAndAlerting",
                label: "Events and Alerting",
              }),
            },
            element: S(ProductsEventsAndAlerting),
          },
          {
            path: "professionalBuilder",
            handle: {
              crumb: () => ({
                href: "/products/professionalBuilder",
                label: "Professional Builder",
              }),
            },
            element: S(ProductsProfessionalBuilder),
          },
        ],
      },
    ],
  },
];

export default routes;
